import React, { Suspense } from 'react'
import { Outlet, Route, Routes, useLocation } from 'react-router-dom'
import AuthCheck from '@/web-routes/auth-check'
import AppLayout from '@/layouts/AppLayout'
import { scrollToTop } from '@/utils/app-helper'
import { MY_ORDERS_SCROLL_KEY } from '@/utils/order-status'
import AuthLayout from '@/layouts/auth-layout'
import OrderDetails from '@/pages/account/my-orders/order-details'
import PageLoading from '@/components/conatiners/page-loading'
import NoPageFound from '@/components/no-page-found'
import { Toaster } from 'react-hot-toast'
import { stripStorefrontPrefix } from '@/utils/storefront-paths'
import LegacyMenuBrowseRedirect from '@/web-routes/legacy-menu-browse-redirect'

const HomepagePage = React.lazy(() => import('@/pages/storefront/HomepagePage'))
const Cart = React.lazy(() => import('@/pages/cart'))
const Account = React.lazy(() => import('@/pages/account'))
const HelpSupport = React.lazy(() => import('@/pages/account/help'))
const OrderCheckout = React.lazy(() => import('@/pages/order-checkout'))
const CheckoutSuccess = React.lazy(() => import('@/pages/order-checkout/checkout-success'))
const CheckoutRecover = React.lazy(() => import('@/pages/checkout/recover'))
// const Auth = React.lazy(() => import('@/pages/auth'))
const Login = React.lazy(() => import('@/pages/auth/login'))
const Register = React.lazy(() => import('@/pages/auth/register'))
const ForgotPassword = React.lazy(() => import('@/pages/auth/forgot-password'))
const ResetPassword = React.lazy(() => import('@/pages/auth/reset-password'))
const GoogleCallback = React.lazy(() => import('@/pages/auth/google-callback'))
const FacebookCallback = React.lazy(() => import('@/pages/auth/facebook-callback'))
const AuthCallback = React.lazy(() => import('@/pages/auth/auth-callback'))
const Categories = React.lazy(() => import('@/pages/categories'))
const Dishes = React.lazy(() => import('@/pages/dishes'))

const MyOrders = React.lazy(() => import('@/pages/account/my-orders'))
const MyAddresses = React.lazy(() => import('@/pages/account/my-addresses'))
const UserCoupons = React.lazy(() => import('@/pages/coupons/user-coupons'))
const PaymentResult = React.lazy(() => import('@/pages/payment/PaymentResult'))

/** Shared storefront child routes for legacy `/` and alias `/r/:slug`. */
function storefrontChildRoutes() {
    return (
        <>
            <Route index element={<Suspense fallback={<PageLoading />}><HomepagePage /></Suspense>} />
            <Route path="menu-browse" element={<LegacyMenuBrowseRedirect />} />

            <Route path="" element={<Suspense fallback={<PageLoading />}><AuthLayout /></Suspense>}>
                <Route path="login" element={<Suspense fallback={<PageLoading />}><Login /></Suspense>} />
                <Route path="register" element={<Suspense fallback={<PageLoading />}><Register /></Suspense>} />
                <Route path="forgot-password" element={<Suspense fallback={<PageLoading />}><ForgotPassword /></Suspense>} />
                <Route path="reset-password" element={<Suspense fallback={<PageLoading />}><ResetPassword /></Suspense>} />
                <Route path="oauth/google/complete" element={<Suspense fallback={<PageLoading />}><GoogleCallback /></Suspense>} />
                <Route path="oauth/facebook/complete" element={<Suspense fallback={<PageLoading />}><FacebookCallback /></Suspense>} />
                <Route path="auth/callback" element={<Suspense fallback={<PageLoading />}><AuthCallback /></Suspense>} />
            </Route>

            <Route path="dishes" element={<Suspense fallback={<PageLoading />}><Dishes /></Suspense>} />
            <Route path="categories" element={<Suspense fallback={<PageLoading />}><Categories /></Suspense>} />
            <Route path="cart" element={<Suspense fallback={<PageLoading />}><Cart /></Suspense>} />
            <Route path="payment-result" element={<Suspense fallback={<PageLoading />}><PaymentResult /></Suspense>} />

            <Route element={<AuthCheck />}>
                <Route path="checkout" element={<Suspense fallback={<PageLoading />}><OrderCheckout /></Suspense>} />
                <Route path="checkout/success" element={<Suspense fallback={<PageLoading />}><CheckoutSuccess /></Suspense>} />
                <Route path="checkout/recover" element={<Suspense fallback={<PageLoading />}><CheckoutRecover /></Suspense>} />
                <Route path="user-coupons" element={<Suspense fallback={<PageLoading />}><UserCoupons /></Suspense>} />
                <Route path="my-account" element={<Outlet />} >
                    <Route index element={<Suspense fallback={<PageLoading />}><Account /></Suspense>} />
                    <Route path="orders" element={<Suspense fallback={<PageLoading />}><MyOrders /></Suspense>} />
                    <Route path="orders/:orderId" element={<Suspense fallback={<PageLoading />}><OrderDetails /></Suspense>} />
                    <Route path="delivery-addresses" element={<Suspense fallback={<PageLoading />}><MyAddresses /></Suspense>} />
                    <Route path="help" element={<Suspense fallback={<PageLoading />}><HelpSupport /></Suspense>} />
                </Route>
            </Route>

            <Route path="*" element={<NoPageFound />} />
        </>
    )
}

function WebRoutes() {


    const location = useLocation()
    const currentLocation = location.pathname
    const appPath = stripStorefrontPrefix(currentLocation)


    React.useEffect(() => {
        if (appPath === '/my-account/orders') {
            try { if (sessionStorage.getItem(MY_ORDERS_SCROLL_KEY) != null) return } catch (_) {}
        }
        scrollToTop()
    }, [currentLocation, appPath])

    return (
        <>
            <Routes>
                <Route path="*" element={<NoPageFound />} />
                <Route path="/" element={<AppLayout />}>
                    {storefrontChildRoutes()}
                </Route>
                <Route path="/r/:slug" element={<AppLayout />}>
                    {storefrontChildRoutes()}
                </Route>
            </Routes>

            <Toaster />
        </>
    )
}

export default WebRoutes
